import React, { useEffect, useState, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';

/**
 * EmployeeMultiSelect — server-side autocomplete for employees.
 * Debounces search calls to /api/employees/facets and shows label + centro.
 *
 * Props:
 *   value:     [{id, label, centro}]  currently selected
 *   onChange:  (array) => void
 *   token:     JWT
 *   backendUrl: full URL (from env)
 *   testId?:   string
 */
export function EmployeeMultiSelect({
  value = [],
  onChange,
  token,
  backendUrl,
  testId = 'employee-multiselect',
  placeholder = 'Buscar por nombre o ID…',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!token) return;
      setLoading(true);
      try {
        const r = await fetch(`${backendUrl}/api/employees/facets?q=${encodeURIComponent(query)}&limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setResults(await r.json());
      } catch (e) { /* silent */ }
      setLoading(false);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, token, backendUrl]);

  const toggle = (emp) => {
    if (value.some(v => v.id === emp.id)) {
      onChange(value.filter(v => v.id !== emp.id));
    } else {
      onChange([...value, { id: emp.id, label: emp.label, centro: emp.centro }]);
    }
  };
  const clearAll = () => onChange([]);

  const selectedCount = value.length;
  const triggerLabel = selectedCount === 0
    ? 'Empleados'
    : selectedCount === 1
      ? value[0].label
      : `${selectedCount} empleados`;

  const styles = {
    trigger: {
      minWidth: 220, maxWidth: 340,
      padding: '8px 12px',
      background: '#1e293b',
      border: '1px solid #334155',
      color: '#f1f5f9',
      borderRadius: 6,
      fontSize: 13,
      cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 8,
      justifyContent: 'space-between',
    },
    badge: {
      padding: '1px 8px', borderRadius: 10,
      background: '#60a5fa', color: '#0f172a',
      fontSize: 11, fontWeight: 600,
    },
    popover: {
      width: 380,
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      overflow: 'hidden',
      zIndex: 1000,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      background: '#0f172a',
      border: 'none',
      borderBottom: '1px solid #334155',
      color: '#f1f5f9',
      fontSize: 13,
      outline: 'none',
    },
    optionRow: (selected) => ({
      padding: '8px 12px',
      cursor: 'pointer',
      fontSize: 13,
      color: '#f1f5f9',
      display: 'flex', alignItems: 'center', gap: 8,
      background: selected ? 'rgba(96, 165, 250, 0.08)' : 'transparent',
    }),
    checkbox: (checked) => ({
      width: 14, height: 14, borderRadius: 3,
      border: `1.5px solid ${checked ? '#60a5fa' : '#64748b'}`,
      background: checked ? '#60a5fa' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }),
    centro: { color: '#94a3b8', fontSize: 11, marginLeft: 'auto' },
    footer: {
      padding: '8px 12px',
      borderTop: '1px solid #334155',
      display: 'flex', justifyContent: 'space-between',
      background: '#273449',
      fontSize: 12, color: '#94a3b8',
    },
    footerBtn: {
      background: 'transparent', border: 'none', color: '#60a5fa',
      cursor: 'pointer', fontSize: 12, padding: '2px 6px',
    },
    pill: {
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px 2px 10px', borderRadius: 12,
      background: 'rgba(96, 165, 250, 0.14)',
      border: '1px solid rgba(96, 165, 250, 0.3)',
      color: '#dbeafe', fontSize: 11,
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button style={styles.trigger} data-testid={`${testId}-trigger`}>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: selectedCount === 0 ? '#94a3b8' : '#f1f5f9',
            }}>
              <span style={{ color: '#94a3b8', marginRight: 6 }}>Empleado:</span>
              {triggerLabel}
            </span>
            {selectedCount > 0 && <span style={styles.badge}>{selectedCount}</span>}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="start" sideOffset={4} style={styles.popover}>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={placeholder}
              style={styles.input}
              data-testid={`${testId}-search`}
            />
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>Buscando…</div>
              ) : results.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                  {query ? 'Sin resultados' : 'Escribe para buscar empleados…'}
                </div>
              ) : (
                results.map(emp => {
                  const selected = value.some(v => v.id === emp.id);
                  return (
                    <div
                      key={emp.id}
                      style={styles.optionRow(selected)}
                      onClick={() => toggle(emp)}
                      data-testid={`${testId}-option-${emp.id}`}
                    >
                      <span style={styles.checkbox(selected)}>
                        {selected && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="4">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {emp.label}
                        </span>
                        <span style={{ color: '#94a3b8', fontSize: 11 }}>
                          {emp.id} {emp.centro && `• ${emp.centro}`}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div style={styles.footer}>
              <span>{selectedCount} seleccionados</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedCount > 0 && (
                  <button onClick={clearAll} style={styles.footerBtn} data-testid={`${testId}-clear`}>
                    Limpiar
                  </button>
                )}
                <button onClick={() => setOpen(false)} style={styles.footerBtn}>Cerrar</button>
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Selected pills */}
      {selectedCount > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 380 }}>
          {value.slice(0, 4).map(v => (
            <span key={v.id} style={styles.pill}>
              {v.label}
              <button
                onClick={() => onChange(value.filter(x => x.id !== v.id))}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
                aria-label="Quitar"
                data-testid={`${testId}-pill-remove-${v.id}`}
              >×</button>
            </span>
          ))}
          {selectedCount > 4 && <span style={{ ...styles.pill, background: 'transparent', border: '1px dashed #334155' }}>+{selectedCount - 4} más</span>}
        </div>
      )}
    </div>
  );
}

export default EmployeeMultiSelect;
